/**
 * Skeleton loading components — shimmer placeholders for data-heavy pages.
 * Use these instead of spinners for perceived performance.
 */

function Shimmer({ className = "" }) {
  return (
    <div className={`animate-pulse bg-gradient-to-r from-[var(--bg-overlay)] via-[var(--bg-muted)] to-[var(--bg-overlay)] bg-[length:200%_100%] rounded ${className}`}
      style={{ animation: "skeleton-shimmer 1.5s ease-in-out infinite" }}
    />
  );
}

/** Skeleton for a storyboard frame card */
export function FrameSkeleton() {
  return (
    <div className="rounded-2xl overflow-hidden border border-[var(--border-subtle)] bg-[var(--bg-elevated)]">
      <Shimmer className="aspect-video w-full" />
      <div className="p-3 space-y-2">
        <div className="flex items-center gap-2">
          <Shimmer className="h-3 w-16" />
          <Shimmer className="h-3 w-10" />
        </div>
        <Shimmer className="h-2.5 w-24" />
      </div>
    </div>
  );
}

/** Skeleton grid for storyboard page */
export function StoryboardSkeleton({ count = 8 }) {
  return (
    <div className="space-y-10 p-6">
      {/* Scene header skeleton */}
      <div className="flex items-center gap-4">
        <div className="h-px flex-1 bg-[var(--border-subtle)]" />
        <Shimmer className="h-5 w-20 rounded-full" />
        <Shimmer className="h-3 w-12" />
        <div className="h-px flex-1 bg-[var(--border-subtle)]" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
        {Array.from({ length: count }).map((_, i) => (
          <FrameSkeleton key={i} />
        ))}
      </div>
    </div>
  );
}

/** Skeleton for a project card on Dashboard */
export function ProjectCardSkeleton() {
  return (
    <div className="rounded-2xl overflow-hidden border border-[var(--border-subtle)] bg-[var(--bg-elevated)]">
      <Shimmer className="aspect-[16/10] w-full" />
      <div className="p-4 space-y-3">
        <Shimmer className="h-4 w-3/4" />
        <Shimmer className="h-3 w-full" />
        <div className="flex justify-between items-center pt-2">
          <Shimmer className="h-3 w-16" />
          <Shimmer className="h-8 w-16 rounded-lg" />
        </div>
      </div>
    </div>
  );
}

/** Skeleton grid for dashboard */
export function DashboardSkeleton() {
  return (
    <div className="space-y-8">
      {/* Hero skeleton */}
      <div className="space-y-3">
        <Shimmer className="h-8 w-64" />
        <Shimmer className="h-4 w-48" />
      </div>
      {/* Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {Array.from({ length: 3 }).map((_, i) => (
          <ProjectCardSkeleton key={i} />
        ))}
      </div>
    </div>
  );
}

/** Skeleton for character/location cards */
export function ItemCardSkeleton() {
  return (
    <div className="flex gap-3 p-3 rounded-xl border border-[var(--border-subtle)] bg-[var(--bg-elevated)]">
      <Shimmer className="w-16 h-16 rounded-lg flex-shrink-0" />
      <div className="flex-1 space-y-2 py-1">
        <Shimmer className="h-3.5 w-2/3" />
        <Shimmer className="h-2.5 w-full" />
        <Shimmer className="h-2.5 w-1/2" />
      </div>
    </div>
  );
}

/** Generic inline skeleton bar */
export function InlineSkeleton({ width = "100%", height = "16px" }) {
  return <Shimmer className="rounded" style={{ width, height }} />;
}
